import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useRef, useState } from "react";
import ToolTipUsingPortal from "@/components/toolTipUsingPortal/toolTipUsingPortal";

const PRIMARY_SITE_MAX_CHARACTERS = 30;

const DiagnosisSummaryRowDivider = () => {
  return (
    <div>
      <svg
        width="1"
        height="46"
        viewBox="0 0 1 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="0.5" x2="0.5" y2="46" stroke="#E6E6E6" />
      </svg>
    </div>
  );
};

export interface DiagnosisSummary {
  name: string;
  details: string;
  stage: string | null;
  metastaticSites: string[];
}

const DEFAULT_DATA: DiagnosisSummary = {
  name: "Non-Small Cell Lung Cancer",
  details: "Non Squamous • Diagnosed 2021-01-01",
  stage: "III B",
  metastaticSites: ["Mediastinal Lymph Node", "Bone", "Adrenal Gland"],
};

/**
 * Diagnosis summary row: diagnosis name/details, stage, and metastatic
 * sites shown in the left section of the diagnosis card.
 *
 * @returns Diagnosis summary row
 */
const DiagnosisSummaryRow = ({
  data = DEFAULT_DATA,
}: {
  data?: DiagnosisSummary;
}) => {
  const metastaticSites = data.metastaticSites.filter((site) => {
    const normalizedSite = site.trim();
    return normalizedSite.length > 0 && !["-", "—"].includes(normalizedSite);
  });
  const [primarySite, ...remainingSites] = metastaticSites;
  const isPrimarySiteTruncated = Boolean(
    primarySite && primarySite.length > PRIMARY_SITE_MAX_CHARACTERS,
  );
  const primarySiteLabel = isPrimarySiteTruncated
    ? `${primarySite.slice(0, PRIMARY_SITE_MAX_CHARACTERS).trimEnd()}…`
    : primarySite;
  const diagnosisDetails = ["-", "—"].includes(data.details.trim())
    ? ""
    : data.details;
  const normalizedStage = data.stage?.trim();
  const stage =
    !normalizedStage || ["-", "—"].includes(normalizedStage)
      ? "Unknown Stage"
      : data.stage;
  const moreSitesRef = useRef<HTMLDivElement>(null);
  const [showMoreSitesTooltip, setShowMoreSitesTooltip] = useState(false);
  const primarySiteRef = useRef<HTMLDivElement>(null);
  const [showPrimarySiteTooltip, setShowPrimarySiteTooltip] = useState(false);

  return (
    <div className="bg-white flex gap-6 single-diagnosis-card">
      <div className="diagnosis-details-container flex-1 min-w-0 flex flex-col gap-1 justify-between">
        <div className="diagnosis-main-name truncate  font-semibold text-[1rem]">
          {data.name}
        </div>
        <div className="diagnosis-sub-details truncate text-tiny text-[#5C5C5C] font-normal leading-6 tracking-[0.0125rem]">
          {diagnosisDetails}
        </div>
      </div>
      <DiagnosisSummaryRowDivider />
      <div className="stage-container flex flex-col self-center">
        <div className="stage-header text-[#5C5C5C] text-overline uppercase font-semibold leading-4 tracking-[0.01563rem] mb-[0.38rem]">
          Stage
        </div>
        <div className="stage-info text-primaryGray-1 text-sm font-normal leading-snug text-right">
          {stage}
        </div>
      </div>
      <DiagnosisSummaryRowDivider />
      <div className="metastatic-sites-container flex flex-col self-center w-1/5 min-w-0">
        <div className="metastatic-sites-header text-[#5C5C5C] text-overline uppercase font-semibold leading-4 tracking-[0.01563rem] mb-[0.38rem] self-end">
          Metastatic Sites
        </div>
        <div className="metastatic-sites-info flex gap-1 items-center self-end">
          {primarySite ? (
            <>
              <div
                ref={primarySiteRef}
                className="min-w-0 max-w-[12rem]"
                tabIndex={0}
                aria-label={primarySite}
                onMouseEnter={() => setShowPrimarySiteTooltip(true)}
                onMouseLeave={() => setShowPrimarySiteTooltip(false)}
                onFocus={() => setShowPrimarySiteTooltip(true)}
                onBlur={() => setShowPrimarySiteTooltip(false)}
              >
                <Tags
                  label={primarySiteLabel || primarySite}
                  shouldTruncate
                  className="max-w-full"
                />
              </div>
              <ToolTipUsingPortal
                placement="top"
                showTooltip={showPrimarySiteTooltip}
                parentRef={primarySiteRef}
                wrapText
                maxWidth="320px"
              >
                {primarySite}
              </ToolTipUsingPortal>
            </>
          ) : (
            <Tags
              label="No Metastasis"
              backgroundColor="#F5F5F5"
              textColor="#0F0F0F"
            />
          )}
          {remainingSites.length > 0 && (
            <>
              <div
                ref={moreSitesRef}
                className="additional-sites text-tiny italic leading-4 font-normal hover:cursor-pointer"
                onMouseEnter={() => setShowMoreSitesTooltip(true)}
                onMouseLeave={() => setShowMoreSitesTooltip(false)}
              >
                +{remainingSites.length} More
              </div>
              <ToolTipUsingPortal
                placement="top"
                showTooltip={showMoreSitesTooltip}
                parentRef={moreSitesRef}
              >
                <div className="flex flex-wrap items-center gap-1">
                  {remainingSites.map((site, index) => (
                    <Tags key={index} label={site} />
                  ))}
                </div>
              </ToolTipUsingPortal>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosisSummaryRow;
